import { getPathInfo } from '@sveltia/utils/file';
import mime from 'mime';

import { fetchLastCommit } from '$lib/services/backends/git/github/commits';
import {
  getWorkflowRepository,
  initOpenAuthoring,
  isOpenAuthoringConfigured,
} from '$lib/services/backends/git/github/fork';
import { fetchAliasedBatch } from '$lib/services/backends/git/github/graphql';
import {
  checkBranchAccess,
  checkRepositoryAccess,
  fetchDefaultBranchName,
  repository,
} from '$lib/services/backends/git/github/repository';
import { fetchAPI } from '$lib/services/backends/git/shared/api';
import { runConcurrently } from '$lib/services/backends/git/shared/concurrency';
import { fetchAndParseFiles } from '$lib/services/backends/git/shared/fetch';
import { startSimulatedProgress } from '$lib/services/backends/git/shared/progress';
import { encodePath } from '$lib/services/backends/git/shared/url';
import { forkedRepository, openAuthoringInitialized } from '$lib/services/workflow/open-authoring';

/**
 * @import {
 * Asset,
 * BaseFileListItem,
 * BaseFileListItemProps,
 * RepositoryContentsMap,
 * } from '$lib/types/private';
 * @import { RepositoryMetadataMap } from '$lib/services/backends/git/shared/fetch';
 */

/**
 * Fetch the repository’s complete file list, and return it in the canonical format.
 * @param {string} [lastHash] The last commit’s SHA-1 hash.
 * @returns {Promise<BaseFileListItemProps[]>} File list.
 */
export const fetchFileList = async (lastHash) => {
  const { owner, repo, branch } = repository;
  const ref = encodePath(/** @type {string} */ (lastHash ?? branch));

  const result =
    /** @type {{ tree: { type: string, path: string, sha: string, size: number }[] }} */ (
      await fetchAPI(`/repos/${owner}/${repo}/git/trees/${ref}?recursive=1`)
    );

  return result.tree
    .filter(({ type }) => type === 'blob')
    .map(({ path, sha, size }) => ({ path, sha, size, name: getPathInfo(path).basename }));
};

/**
 * Number of files requested per GraphQL query. The API has no hard limit on aliases, but a bigger
 * query takes longer to answer and is more likely to time out.
 */
const CHUNK_SIZE = 250;

/**
 * Get the GraphQL field selection for fetching the text content of the given file.
 * @param {BaseFileListItem} file File.
 * @returns {string} Field selection, or an empty string for an asset, which has no content to read
 * and is just listed for its metadata.
 */
export const getFileContentsFragment = ({ type, sha }) =>
  type === 'asset'
    ? ''
    : `object(oid: ${JSON.stringify(sha)}) { ... on Blob { text isTruncated } }`;

/**
 * Get the GraphQL field selection for fetching the last commit of the given file.
 * @param {BaseFileListItem} file File.
 * @returns {string} Field selection.
 */
export const getFileMetadataFragment = ({ path }) => `
  ref(qualifiedName: $branch) {
    target {
      ... on Commit {
        history(first: 1, path: ${JSON.stringify(path)}) {
          nodes {
            author {
              name
              email
              user {
                id: databaseId
                login
              }
            }
            committedDate
          }
        }
      }
    }
  }
`;

/**
 * Request a blob from the REST API. The `raw` media type asks for the file content itself, rather
 * than the base64-wrapped JSON the endpoint returns by default.
 * @param {object} args Arguments.
 * @param {string} args.owner Repository owner.
 * @param {string} args.repo Repository name.
 * @param {string} args.sha Blob SHA-1 hash.
 * @param {'text' | 'raw'} args.responseType Whether to read the content as text, or to hand the
 * `Response` back so the caller can decide. Note that `raw` bypasses the shared error handling, so
 * the caller has to check the status itself.
 * @returns {Promise<string | Response>} File content or response, depending on `responseType`.
 * @see https://docs.github.com/en/rest/git/blobs#get-a-blob
 */
const requestBlob = async ({ owner, repo, sha, responseType }) =>
  /** @type {Promise<string | Response>} */ (
    fetchAPI(`/repos/${owner}/${repo}/git/blobs/${sha}`, {
      headers: { Accept: 'application/vnd.github.raw' },
      responseType,
    })
  );

/**
 * Retrieve the text content of a blob with the REST API, which returns it in full. The GraphQL API
 * cuts `Blob.text` off at 512 KB and reports it with `isTruncated`; keeping that shortened text
 * would silently drop the tail of the file the next time the entry is saved.
 * @param {object} args Arguments.
 * @param {string} args.owner Repository owner.
 * @param {string} args.repo Repository name.
 * @param {string} args.sha Blob SHA-1 hash.
 * @returns {Promise<string>} File content.
 * @see https://github.com/sveltia/sveltia-cms/issues/950
 * @see https://github.com/graphql-hive/graphql-inspector/issues/2079
 */
export const fetchBlobText = async ({ owner, repo, sha }) =>
  /** @type {Promise<string>} */ (requestBlob({ owner, repo, sha, responseType: 'text' }));

/**
 * Parse the file contents from the API response.
 * @param {BaseFileListItem[]} fetchingFiles Base file list.
 * @param {any[]} results Result for each file from the API, in the same order.
 * @returns {Promise<RepositoryContentsMap>} Parsed file contents map. The commit metadata is left
 * out; it’s fetched separately with {@link fetchFileMetadata}.
 */
export const parseFileContents = async (fetchingFiles, results) => {
  /** @type {{ sha: string, data: { text?: string } }[]} */
  const truncatedFiles = [];

  const entries = fetchingFiles.map(({ path, sha, size }, index) => {
    const { text, isTruncated } = results[index] ?? {};
    const data = { sha, size: /** @type {number} */ (size), text, meta: undefined };

    if (isTruncated) {
      truncatedFiles.push({ sha, data });
    }

    return [path, data];
  });

  // Read any oversized blob again with the REST API, which has no such size cap
  const { owner, repo } = repository;

  await runConcurrently(truncatedFiles, async ({ sha, data }) => {
    data.text = await fetchBlobText({ owner, repo, sha });
  });

  return Object.fromEntries(entries);
};

/**
 * Parse the file metadata from the API response.
 * @param {BaseFileListItem[]} fetchingFiles Base file list.
 * @param {any[]} results Result for each file from the API, in the same order.
 * @returns {RepositoryMetadataMap} Parsed file metadata map.
 */
export const parseFileMetadata = (fetchingFiles, results) =>
  Object.fromEntries(
    fetchingFiles.map(({ path }, index) => {
      const {
        author: { name, email, user: _user },
        committedDate,
      } = results[index].target.history.nodes[0];

      return [
        path,
        {
          commitAuthor: { name, email, id: _user?.id, login: _user?.login },
          commitDate: new Date(committedDate),
        },
      ];
    }),
  );

/**
 * Query the API for the given files, a chunk at a time to avoid an API timeout, with a limited
 * number of requests in flight at once to avoid a Too Many Requests error.
 * @param {BaseFileListItem[]} fetchingFiles Base file list.
 * @param {string} alias Alias prefix.
 * @param {(file: BaseFileListItem) => string} getFragment Function to build the field selection
 * for a file.
 * @param {object} [options] Options.
 * @param {boolean} [options.useBranch] Whether the field selection uses the `$branch` variable.
 * @returns {Promise<any[]>} Result for each file, in the same order as `fetchingFiles`.
 */
const fetchInChunks = (fetchingFiles, alias, getFragment, { useBranch = false } = {}) =>
  fetchAliasedBatch({ items: fetchingFiles, alias, getFragment, chunkSize: CHUNK_SIZE, useBranch });

/**
 * Fetch the text contents of entry/config files. The commit metadata of the files is not included;
 * see {@link fetchFileMetadata}.
 * @param {BaseFileListItem[]} fetchingFiles Base file list.
 * @returns {Promise<RepositoryContentsMap>} Fetched contents map.
 */
export const fetchFileContents = async (fetchingFiles) => {
  // Show a simulated progress bar because the request waiting time is long
  const stopProgress = startSimulatedProgress(fetchingFiles.length);
  /** @type {any[]} */
  let results;

  try {
    results = await fetchInChunks(fetchingFiles, 'content', getFileContentsFragment);
  } finally {
    // Also on failure, so the interval doesn’t keep running behind the error message
    stopProgress();
  }

  return parseFileContents(fetchingFiles, results);
};

/**
 * Fetch the last commit of each entry/asset file. Looking up the history of every path is the slow
 * part of a cold start — far slower than reading the blobs — so it’s done in this second pass,
 * after the contents have been shown.
 * @param {BaseFileListItem[]} fetchingFiles Base file list.
 * @returns {Promise<RepositoryMetadataMap>} Fetched metadata map.
 */
export const fetchFileMetadata = async (fetchingFiles) =>
  parseFileMetadata(
    fetchingFiles,
    await fetchInChunks(fetchingFiles, 'commit', getFileMetadataFragment, { useBranch: true }),
  );

/**
 * Fetch file list from the backend service, download/parse all the entry files, then cache them in
 * the {@link allEntries} and {@link allAssets} stores.
 */
export const fetchFiles = async () => {
  // With Open Authoring, a user without write access is a contributor rather than a stranger, so
  // they’re given a fork to work in instead of being turned away. Setting the fork up may involve
  // the user, so it has to finish before the data is fetched, unlike a plain access check
  const openAuthoring = isOpenAuthoringConfigured();

  // Once only: a later call brings the stores up to date with the repository, and setting the fork
  // up again would reset the fork state while a workflow commit may be relying on it
  if (openAuthoring && !openAuthoringInitialized.current) {
    await initOpenAuthoring();
  }

  await fetchAndParseFiles({
    repository,
    checkAccess: openAuthoring ? undefined : checkRepositoryAccess,
    // A contributor’s changes go to their fork, so the branch they can’t push to doesn’t matter
    checkBranchAccess: forkedRepository.current ? undefined : checkBranchAccess,
    fetchDefaultBranchName,
    fetchLastCommit,
    fetchFileList,
    fetchFileContents,
    fetchFileMetadata,
  });
};

/**
 * Fetch an asset as a Blob via the API.
 * @param {Asset} asset Asset to retrieve the file content.
 * @returns {Promise<Blob>} Blob data.
 */
export const fetchBlob = async (asset) => {
  // An asset attached to an unpublished entry is committed to a workflow branch, which lives in the
  // contributor’s fork with Open Authoring, so the blob has to be read from there
  const { owner, repo } = asset.workflow ? getWorkflowRepository() : repository;
  const { sha, path } = asset;

  const response = /** @type {Response} */ (
    await requestBlob({ owner, repo, sha, responseType: 'raw' })
  );

  // A `raw` response skips the shared error handling, so an error body would otherwise be returned
  // as the file content, and moving or renaming the asset would commit it in place of the file
  if (!response.ok) {
    throw new Error('Failed to fetch the blob', { cause: { status: response.status } });
  }

  // Handle SVG and other non-binary files
  if (response.headers.get('Content-Type') !== 'application/octet-stream') {
    return new Blob([await response.text()], { type: mime.getType(path) ?? 'text/plain' });
  }

  return response.blob();
};
